import React from "react";
import { Link } from "react-router-dom";
import { openExternalUrl } from "../../utils/safeUrl";
import { resolveUrl } from "../../utils/urls";

function labelsToText(v) {
  if (!v) return "";
  if (Array.isArray(v)) {
    return v
      .map((x) => (typeof x === "string" ? x : (x?.name || x?.label || x?.title || "")))
      .filter(Boolean)
      .join(", ");
  }
  if (typeof v === "string") return v;
  if (typeof v === "object") return v.name || v.label || v.title || "";
  return String(v);
}

function firstText(...values) {
  for (const value of values) {
    const text = String(value ?? "").replace(/\s+/g, " ").trim();
    if (text) return text;
  }
  return "";
}

function firstNumber(...values) {
  for (const value of values) {
    if (value == null || value === "") continue;
    const n = Number(value);
    if (Number.isFinite(n)) return n;
  }
  return null;
}

function formatMoney(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "цена по запросу";
  return new Intl.NumberFormat("ru-RU", {
    style: "currency",
    currency: "RUB",
    maximumFractionDigits: 0,
  }).format(n);
}

function parseDate(value) {
  if (!value) return null;
  const d = value instanceof Date ? value : new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

function formatDateLabel(startValue) {
  const d = parseDate(startValue);
  if (!d) return "—";
  return new Intl.DateTimeFormat("ru-RU", {
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  })
    .format(d)
    .replace(/\.$/, "");
}

function formatLongDateLabel(startValue, endValue) {
  const start = parseDate(startValue);
  if (!start) return "—";
  const end = parseDate(endValue);
  const day = new Intl.DateTimeFormat("ru-RU", {
    weekday: "long",
    day: "numeric",
    month: "long",
  }).format(start);
  const from = new Intl.DateTimeFormat("ru-RU", { hour: "2-digit", minute: "2-digit" }).format(start);
  const to = end ? new Intl.DateTimeFormat("ru-RU", { hour: "2-digit", minute: "2-digit" }).format(end) : "";
  return `${day}, ${to ? `${from}–${to}` : from}`;
}

function getInitials(name) {
  const text = firstText(name);
  if (!text) return "П";
  const parts = text.split(/\s+/).filter(Boolean);
  return ((parts[0]?.[0] || "") + (parts[1]?.[0] || "")).toUpperCase() || text.slice(0, 2).toUpperCase();
}

const PAID_STATUSES = new Set(["PAID", "CONFIRMED", "SUCCEEDED"]);
function isPaidStatus(status) {
  return PAID_STATUSES.has(String(status || "").toUpperCase());
}

function extractReviews(session) {
  const raw = session?.raw || session || {};
  const list = raw.reviews || raw.testimonials || raw.feedback || raw.feedbacks || session?.reviews || [];
  if (!Array.isArray(list)) return [];
  return list
    .map((item, index) => {
      const text = firstText(item?.text, item?.content, item?.message, item?.review);
      if (!text) return null;
      const name = firstText(item?.authorName, item?.author_name, item?.name, item?.clientName, item?.userName);
      return {
        id: item?.id ?? index,
        text,
        name: name || "Участник сессии",
        role: firstText(item?.role, item?.subtitle, item?.caption) || "участник сессии",
        avatar: firstText(item?.avatarUrl, item?.avatar_url, item?.photoUrl, item?.image),
      };
    })
    .filter(Boolean)
    .slice(0, 3);
}

function pickFormat(session) {
  const raw = session?.raw || {};
  return firstText(session?.formatLabel, session?.format, raw.formatLabel, raw.format_label, raw.format, raw.sessionFormat, raw.session_format) || "Онлайн";
}

function pickHost(session) {
  const raw = session?.raw || {};
  const psychologist = raw.psychologist || raw.owner || raw.author || {};
  const name = firstText(session?.name, session?.psyName, psychologist?.name, raw.psychologistName, raw.psychologist_name, "Психолог");
  const avatar = firstText(
    session?.psyAvatarUrl,
    session?.psychologistAvatarUrl,
    session?.avatarUrl,
    psychologist?.avatarUrl,
    psychologist?.avatar_url,
    psychologist?.photoUrl,
    psychologist?.photo_url,
    raw.psychologistAvatarUrl,
    raw.psychologist_avatar_url
  );
  const about = firstText(
    session?.psyAbout,
    session?.psychologistAbout,
    psychologist?.shortBio,
    psychologist?.short_bio,
    psychologist?.about,
    psychologist?.description,
    raw.psychologistAbout,
    raw.psychologist_about
  );
  const experienceYears = firstNumber(
    session?.psyExperienceYears,
    session?.experienceYears,
    psychologist?.experienceYears,
    psychologist?.experience_years,
    raw.psychologistExperienceYears,
    raw.psychologist_experience_years
  );
  const experience = firstText(
    session?.psyExperience,
    session?.experience,
    psychologist?.experience,
    raw.psychologistExperience,
    raw.psychologist_experience,
    experienceYears != null ? `${experienceYears}+ лет опыта работы` : ""
  );
  const specialization = labelsToText(psychologist?.specializations || raw.psychologistSpecializations || raw.specializations);

  return { name, avatar, about, experience, specialization };
}

function Icon({ name, size = 20 }) {
  const common = {
    width: size,
    height: size,
    viewBox: "0 0 24 24",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg",
    "aria-hidden": true,
  };
  const strokeProps = { stroke: "currentColor", strokeWidth: 1.9, strokeLinecap: "round", strokeLinejoin: "round" };

  if (name === "calendar") return <svg {...common}><path {...strokeProps} d="M7 3v3M17 3v3M4.5 9h15M6.5 5h11A2.5 2.5 0 0 1 20 7.5v10A2.5 2.5 0 0 1 17.5 20h-11A2.5 2.5 0 0 1 4 17.5v-10A2.5 2.5 0 0 1 6.5 5Z" /></svg>;
  if (name === "wifi") return <svg {...common}><path {...strokeProps} d="M5 10.5a10 10 0 0 1 14 0M8 14a5.8 5.8 0 0 1 8 0M11 17.5a1.4 1.4 0 0 1 2 0" /><path {...strokeProps} d="M12 19h.01" /></svg>;
  if (name === "user") return <svg {...common}><path {...strokeProps} d="M12 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8ZM4.5 20a7.5 7.5 0 0 1 15 0" /></svg>;
  if (name === "clock") return <svg {...common}><path {...strokeProps} d="M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18ZM12 7v5l3 2" /></svg>;
  if (name === "users") return <svg {...common}><path {...strokeProps} d="M16.5 19a5.5 5.5 0 0 0-9 0M12 12a3.4 3.4 0 1 0 0-6.8 3.4 3.4 0 0 0 0 6.8ZM18 11.5a2.8 2.8 0 0 0 .2-5.5M20.5 18a4.8 4.8 0 0 0-3.1-4.1M6 11.5A2.8 2.8 0 0 1 5.8 6M3.5 18a4.8 4.8 0 0 1 3.1-4.1" /></svg>;
  if (name === "tag") return <svg {...common}><path {...strokeProps} d="m20 12-8 8-8.5-8.5V4H11l9 8Z" /><path {...strokeProps} d="M7.5 7.5h.01" /></svg>;
  if (name === "lock") return <svg {...common}><path {...strokeProps} d="M7 10V8a5 5 0 0 1 10 0v2M6.5 10h11A1.5 1.5 0 0 1 19 11.5v7A1.5 1.5 0 0 1 17.5 20h-11A1.5 1.5 0 0 1 5 18.5v-7A1.5 1.5 0 0 1 6.5 10Z" /></svg>;
  if (name === "shield") return <svg {...common}><path {...strokeProps} d="M12 21s7-3.5 7-10V5.5L12 3 5 5.5V11c0 6.5 7 10 7 10Z" /><path {...strokeProps} d="m9.5 12 1.7 1.7 3.8-4" /></svg>;
  if (name === "leaf") return <svg {...common}><path {...strokeProps} d="M20 4s-8.5-.7-13 3.8C3.4 11.4 4 17 4 17s5.6.6 9.2-3C17.7 9.5 20 4 20 4Z" /><path {...strokeProps} d="M4 20c3-6 7-9 12-12" /></svg>;
  if (name === "message") return <svg {...common}><path {...strokeProps} d="M5 18.5V7.2A3.2 3.2 0 0 1 8.2 4h7.6A3.2 3.2 0 0 1 19 7.2v4.6a3.2 3.2 0 0 1-3.2 3.2H9l-4 3.5Z" /></svg>;
  if (name === "spark") return <svg {...common}><path {...strokeProps} d="M12 3l1.9 5.1L19 10l-5.1 1.9L12 17l-1.9-5.1L5 10l5.1-1.9L12 3ZM18.5 15l.8 2.2 2.2.8-2.2.8-.8 2.2-.8-2.2-2.2-.8 2.2-.8.8-2.2Z" /></svg>;
  if (name === "card") return <svg {...common}><path {...strokeProps} d="M4.5 7.5h15v9a2 2 0 0 1-2 2h-11a2 2 0 0 1-2-2v-9ZM4.5 10.5h15" /></svg>;
  if (name === "arrow") return <svg {...common}><path {...strokeProps} d="M5 12h14M13 6l6 6-6 6" /></svg>;
  if (name === "star") return <svg {...common}><path {...strokeProps} d="m12 3 2.6 5.3 5.8.8-4.2 4.1 1 5.8-5.2-2.7L6.8 19l1-5.8L3.6 9.1l5.8-.8L12 3Z" /></svg>;
  return <svg {...common}><circle cx="12" cy="12" r="9" {...strokeProps} /><path {...strokeProps} d="M12 8v4l2.5 2" /></svg>;
}

function DetailRow({ icon, label, value, accent }) {
  if (!value) return null;
  return (
    <div className="gs-public-booking__row">
      <span className="gs-public-booking__row-icon"><Icon name={icon} size={19} /></span>
      <span className="gs-public-booking__row-label">{label}</span>
      <strong className={accent ? "is-accent" : ""}>{value}</strong>
    </div>
  );
}

function FeatureCard({ icon, title, text }) {
  return (
    <article className="gs-public-card gs-public-feature">
      <span className="gs-public-card__icon"><Icon name={icon} size={24} /></span>
      <h3>{title}</h3>
      <p>{text}</p>
    </article>
  );
}

export default function GroupSessionDetailsView({ session, onPay, loading = false, payLabel = "Перейти к оплате", payLoading = false, isAuthed = false }) {
  const [paymentCodeType, setPaymentCodeType] = React.useState("PROMO");
  const [paymentCode, setPaymentCode] = React.useState("");
  const [isDiscountOpen, setIsDiscountOpen] = React.useState(false);

  React.useEffect(() => {
    setPaymentCodeType("PROMO");
    setPaymentCode("");
    setIsDiscountOpen(false);
  }, [session?.id]);

  if (!session) return null;

  const raw = session.raw || {};
  const title = firstText(session.title, session.name, raw.title, "Групповая сессия");
  const description = firstText(session.description, raw.description);
  const host = pickHost(session);
  const hostAvatar = host.avatar ? resolveUrl(host.avatar) : "";
  const coverUrl = firstText(session.image, session.coverImg, session.coverUrl, raw.coverImg, raw.cover_img);
  const resolvedCoverUrl = coverUrl ? resolveUrl(coverUrl) : "";
  const formatLabel = pickFormat(session);
  const startDateTime = session.startDateTime || raw.startDateTime || raw.start_datetime || null;
  const endDateTime = session.endDateTime || raw.endDateTime || raw.end_datetime || null;
  const dateShort = formatDateLabel(startDateTime) !== "—" ? formatDateLabel(startDateTime) : firstText(session.date, "—");
  const dateLong = formatLongDateLabel(startDateTime, endDateTime) !== "—" ? formatLongDateLabel(startDateTime, endDateTime) : firstText(session.date, "—");
  const duration = firstText(
    session?.sessionDuration != null ? `${session.sessionDuration} минут` : "",
    session?.sessionDurationMinutes != null ? `${session.sessionDurationMinutes} минут` : ""
  );

  const participantsCount = Number(session?.participantsCount ?? raw.participantsCount ?? raw.clientsCount ?? 0) || 0;
  const paidCount = session?.paidCount != null ? Number(session.paidCount) : raw?.paidCount != null ? Number(raw.paidCount) : null;
  const shownCount = Number.isFinite(paidCount) ? paidCount : participantsCount;
  const capacityClients = firstNumber(session?.capacityClients, raw.capacityClients, raw.capacity_clients);
  const freePlaces = capacityClients != null ? Math.max(0, capacityClients - shownCount) : null;
  const participantsLabel = capacityClients != null ? `${shownCount} / ${capacityClients} мест занято` : `${shownCount} участников`;
  const freePlacesLabel = capacityClients != null ? `${freePlaces} / ${capacityClients} свободных мест` : "Количество мест уточняется";

  const priceValue = session?.pricePerSession ?? session?.priceAtTime ?? raw.priceAtTime ?? raw.price_at_time;
  const priceLabelText = formatMoney(priceValue);
  const themesText = labelsToText(session?.themes || session?.themeLabels || session?.topics || raw.themes || raw.themeLabels);
  const methodText = labelsToText(session?.method || session?.methods || session?.methodLabels || raw.methods || raw.methodLabels);
  const telemostUrl = String(session?.telemostUrl || raw.telemostUrl || raw.telemost_url || "").trim();
  const statusKey = String(session?.status || raw.status || "").toUpperCase();
  const isClosedSession = ["CLOSED", "CANCELLED", "CANCELED", "FINISHED", "COMPLETED"].includes(statusKey);
  const isPaidSession = isPaidStatus(statusKey) || Boolean(session?.isPaid);
  const alreadyJoined =
    !isPaidSession &&
    (Boolean(session?.alreadyJoined) ||
      Boolean(session?.joinedBookingId) ||
      ["JOINED", "BOOKED", "PENDING_PAYMENT", "PENDING"].includes(statusKey));
  const paymentReturnFlag = Boolean(session?.paymentReturn);
  const canSubmit = !(loading || payLoading);
  const reviews = extractReviews(session);

  const primaryButtonLabel = isClosedSession
    ? "Запись закрыта"
    : isPaidSession
      ? (telemostUrl ? "Открыть встречу" : "Ссылка появится позже")
    : alreadyJoined
      ? (telemostUrl ? "Открыть встречу" : "Вы уже записаны")
      : payLoading
        ? "Загрузка…"
        : payLabel === "Перейти к оплате"
          ? (isAuthed ? "Записаться и оплатить" : "Войти и записаться")
          : payLabel;

  const onPrimaryClick = () => {
    if (isPaidSession || (alreadyJoined && telemostUrl)) {
      if (telemostUrl) openExternalUrl(telemostUrl);
      return;
    }
    if (onPay) onPay(session);
  };

  return (
    <main className="gs-public-page">
      <div className="gs-public-page__shell">
        <Link to="/sessions" className="gs-public-back">
          <span aria-hidden="true">←</span>
          Все групповые сессии
        </Link>

        <section className="gs-public-hero" aria-labelledby="group-session-title">
          <div className="gs-public-hero__main">
            <p className="gs-public-eyebrow">Групповая сессия</p>
            <h1 id="group-session-title">{title}</h1>
            {description ? <p className="gs-public-lead">{description}</p> : null}

            <div className="gs-public-badges" aria-label="Ключевые параметры сессии">
              <span><Icon name="wifi" size={16} /> {formatLabel}</span>
              <span><Icon name="shield" size={16} /> {isClosedSession ? "Запись закрыта" : "Открыта запись"}</span>
              <span><Icon name="users" size={16} /> {freePlacesLabel}</span>
            </div>

            <div className="gs-public-cover" aria-hidden="true">
              {resolvedCoverUrl ? <img src={resolvedCoverUrl} alt="" loading="lazy" decoding="async" /> : null}
              <span className="gs-public-cover__orb gs-public-cover__orb--one" />
              <span className="gs-public-cover__orb gs-public-cover__orb--two" />
              <span className="gs-public-cover__wave gs-public-cover__wave--one" />
              <span className="gs-public-cover__wave gs-public-cover__wave--two" />
            </div>
          </div>

          <aside className="gs-public-booking" aria-label="Запись на групповую сессию">
            <DetailRow icon="calendar" label="Дата и время" value={dateShort} />
            <DetailRow icon="wifi" label="Формат" value={formatLabel} />
            <DetailRow icon="user" label="Ведущий" value={host.name} />
            <DetailRow icon="clock" label="Длительность" value={duration || "—"} />
            <DetailRow icon="users" label="Участников" value={participantsLabel} />
            <DetailRow icon="tag" label="Стоимость участия" value={priceLabelText} accent />

            <button
              type="button"
              className="gs-public-booking__button"
              onClick={onPrimaryClick}
              disabled={!canSubmit || isClosedSession || (isPaidSession && !telemostUrl) || (alreadyJoined && !telemostUrl)}
            >
              <Icon name={alreadyJoined || isPaidSession ? "wifi" : "lock"} size={19} />
              {primaryButtonLabel}
            </button>

            {!isPaidSession && !alreadyJoined ? (
              <>
                <p className="gs-public-booking__note">
                  {isAuthed
                    ? "После оплаты участие подтверждается автоматически, а детали появятся в вашем аккаунте."
                    : "Для записи и оплаты войдите в аккаунт. После оплаты участие подтверждается автоматически."}
                </p>

                <div className="gs-public-payment-pills" aria-label="Способы оплаты">
                  <span><Icon name="card" size={17} /> Карта</span>
                  <span>СБП</span>
                  <span><Icon name="shield" size={17} /> Безопасная оплата</span>
                </div>

                <div className={`gs-public-discount ${isDiscountOpen ? "is-open" : ""}`}>
                  {isDiscountOpen ? (
                    <>
                      <select
                        value={paymentCodeType}
                        onChange={(e) => setPaymentCodeType(e.target.value === "CERTIFICATE" ? "CERTIFICATE" : "PROMO")}
                        disabled={!canSubmit}
                        aria-label="Тип кода"
                      >
                        <option value="PROMO">Промокод</option>
                        <option value="CERTIFICATE">Сертификат</option>
                      </select>
                      <input
                        value={paymentCode}
                        onChange={(e) => setPaymentCode(e.target.value)}
                        placeholder="Введите код"
                        disabled={!canSubmit}
                        aria-label={paymentCodeType === "CERTIFICATE" ? "Код сертификата" : "Промокод"}
                      />
                      <button
                        type="button"
                        onClick={() => onPay ? onPay(session, { applyCode: true, discountType: paymentCodeType, discountCode: paymentCode }) : null}
                        disabled={!canSubmit}
                      >
                        Применить
                      </button>
                    </>
                  ) : (
                    <button type="button" onClick={() => setIsDiscountOpen(true)} disabled={!canSubmit}>
                      Добавить промокод или сертификат
                    </button>
                  )}
                </div>
              </>
            ) : null}

            {paymentReturnFlag ? (
              <div className="gs-public-state gs-public-state--success">Оплата/запись принята. Если доступна ссылка — откройте встречу выше.</div>
            ) : alreadyJoined ? (
              <div className="gs-public-state">Вы уже записались на эту групповую сессию.</div>
            ) : null}
          </aside>
        </section>

        <section className="gs-public-grid gs-public-grid--intro">
          <article className="gs-public-card gs-public-about">
            <h2>О сессии</h2>
            {description ? <p>{description}</p> : <p>Подробное описание этой групповой сессии пока не заполнено.</p>}
            <div className="gs-public-tags">
              {themesText ? <span>Темы: {themesText}</span> : null}
              {methodText ? <span>Метод: {methodText}</span> : null}
              {dateLong !== "—" ? <span>{dateLong}</span> : null}
            </div>
          </article>

          <article className="gs-public-card gs-public-host">
            <h2>Ведущий</h2>
            <div className="gs-public-host__row">
              <div className="gs-public-host__avatar">
                {hostAvatar ? <img src={hostAvatar} alt={host.name} loading="lazy" decoding="async" /> : <span>{getInitials(host.name)}</span>}
              </div>
              <div>
                <h3>{host.name}</h3>
                {host.specialization ? <p className="gs-public-host__role">{host.specialization}</p> : <p className="gs-public-host__role">Психолог</p>}
              </div>
            </div>
            {host.about ? <p>{host.about}</p> : null}
            {host.experience ? <span className="gs-public-host__exp">{host.experience}</span> : null}
          </article>
        </section>

        <section className="gs-public-card gs-public-expectations" aria-labelledby="gs-expect-title">
          <div className="gs-public-section-head">
            <h2 id="gs-expect-title">Что вас ждёт</h2>
            <p>Структура встречи помогает сохранять бережный темп и понятные правила участия.</p>
          </div>
          <div className="gs-public-features">
            <FeatureCard icon="shield" title="Безопасное пространство" text="Конфиденциальность и уважительное общение внутри группы." />
            <FeatureCard icon="leaf" title="Практики самоподдержки" text="Дыхание, заземление и простые упражнения для спокойного состояния." />
            <FeatureCard icon="message" title="Обратная связь специалиста" text="Ведущий поддерживает динамику группы и даёт рекомендации." />
            <FeatureCard icon="users" title="Групповая динамика" text="Опыт других участников помогает увидеть свой запрос под новым углом." />
          </div>
        </section>

        <section className="gs-public-grid gs-public-grid--support">
          <article className="gs-public-card gs-public-steps">
            <h2>Как проходит запись</h2>
            <ol>
              <li><span>1</span><strong>Войдите или зарегистрируйтесь</strong><p>Создайте аккаунт, чтобы закрепить место за собой.</p></li>
              <li><span>2</span><strong>Подтвердите запись</strong><p>Нажмите «Записаться» и проверьте параметры сессии.</p></li>
              <li><span>3</span><strong>Оплатите участие</strong><p>Выберите удобный способ оплаты и завершите покупку.</p></li>
              <li><span>4</span><strong>Получите доступ</strong><p>После оплаты информация о сессии появится в вашем аккаунте.</p></li>
            </ol>
            <div className="gs-public-safe-note"><Icon name="lock" size={18} /> После успешной оплаты участие подтверждается автоматически.</div>
          </article>

          <article className="gs-public-card gs-public-faq">
            <h2>Частые вопросы</h2>
            <details>
              <summary>Нужен ли предыдущий опыт?</summary>
              <p>Нет, можно прийти впервые. Ведущий объяснит правила и формат встречи.</p>
            </details>
            <details>
              <summary>Что будет после записи?</summary>
              <p>Запись и оплата сохранятся в вашем аккаунте, а детали участия будут доступны в личном кабинете.</p>
            </details>
            <details>
              <summary>Можно ли участвовать с камерой и микрофоном выключенными?</summary>
              <p>Это зависит от правил конкретной группы. Базовые технические настройки можно проверить перед встречей.</p>
            </details>
            <details>
              <summary>Как проходит оплата?</summary>
              <p>Оплата проходит через подключённый платёжный механизм проекта. Поддерживаются карта, СБП и коды скидок, если они доступны.</p>
            </details>
          </article>
        </section>

        {reviews.length ? (
          <section className="gs-public-card gs-public-reviews" aria-labelledby="gs-reviews-title">
            <div className="gs-public-section-head">
              <h2 id="gs-reviews-title">Отзывы участников</h2>
              <p>Реальные отзывы, полученные для этой сессии.</p>
            </div>
            <div className="gs-public-reviews__list">
              {reviews.map((review) => (
                <article key={review.id}>
                  <p>{review.text}</p>
                  <div>
                    <span className="gs-public-review-avatar">
                      {review.avatar ? <img src={resolveUrl(review.avatar)} alt="" loading="lazy" decoding="async" /> : getInitials(review.name)}
                    </span>
                    <strong>{review.name}</strong>
                    <small>{review.role}</small>
                  </div>
                </article>
              ))}
            </div>
          </section>
        ) : null}

        <section className="gs-public-summary" aria-label="Кратко о сессии">
          <div>
            <span><Icon name="users" size={28} /></span>
            <strong>Групповая сессия — поддержка, которая помогает двигаться вперёд.</strong>
            <p>Вы не одни. Мы рядом.</p>
          </div>
          <dl>
            <div><dt>{freePlaces != null ? freePlaces : "—"}</dt><dd>свободных мест</dd></div>
            <div><dt>{duration || "—"}</dt><dd>длительность</dd></div>
            <div><dt>{formatLabel}</dt><dd>формат</dd></div>
            <div><dt>{priceLabelText}</dt><dd>стоимость</dd></div>
          </dl>
        </section>
      </div>
    </main>
  );
}
